/**
 * Bank Transactions "Accounting" filter dropdown vocabulary. Mirrors
 * backend/services/accounting_status.py's XERO_STATUS_LABELS and
 * PENNYLANE_PUSH_STATE_LABELS exactly -- same mirror-plus-parity-test
 * pattern as transactionCategory.js / enums.TransactionCategory, pinned by
 * backend/tests/test_accounting_status_filter_vocabulary_parity.py.
 *
 * These are the ONLY statuses the filter exposes (not every status
 * derive_accounting_status can emit) -- the Xero reconciliation states and
 * the Pennylane push_state values, per the operator's own filter spec.
 * The `key` values are what routes/transactions.py's `accounting_status`
 * query param expects verbatim.
 */

export const XERO_ACCOUNTING_STATUS_OPTIONS = [
  { key: 'reconciled_in_xero', label: 'Reconciled (Xero)' },
  // Subtitle (CLAUDE.md Prompt W): for an account with real Finance API
  // BankStatementsPlus coverage, this now comes directly from a bank
  // statement line's own isReconciled flag -- ground truth, not the older
  // "coded in Xero, not yet reconciled there" BankTransaction/Payment
  // signal (CLAUDE.md Prompt V item 2), which remains the meaning ONLY for
  // accounts without statement-line coverage (see NO_STATEMENT_LINE_OPTION
  // below and services/accounting_status.py's "source" field).
  { key: 'unreconciled_in_xero', label: 'Unreconciled (Xero)', description: 'Not yet reconciled in Xero -- from a real bank statement line where available, otherwise from a coded-but-unreconciled BankTransaction/Payment.' },
  { key: 'no_xero_record', label: 'No Xero record' },
  { key: 'xero_state_stale', label: 'Xero state stale' },
  // CLAUDE.md Prompt W: a Finance API feed gap -- the mapped account HAS
  // real statement-line coverage, but no line matches this transaction's
  // account+amount+date. Only reachable for statement-line-covered
  // accounts; distinct from no_xero_record (no coverage at all).
  { key: 'no_statement_line', label: 'No statement line (Xero)', description: 'A bank statement line covers this account/window, but none matches this transaction.' },
];

/**
 * "Not yet coded (Xero)" (CLAUDE.md Prompt V item 1) -- a DIFFERENT filter
 * from the accounting_status vocabulary above: not a per-row derived Xero
 * reconciliation status, but a stored-mapping-driven predicate (routes/
 * transactions.py's `not_yet_coded_xero` param, services/xero_accounting_
 * reconciliation.py::build_not_yet_coded_xero_query) for FA transactions on
 * a Xero-mapped account whose last sync found the whole account had zero
 * coded Xero transactions in its pull window -- Xero's API cannot expose an
 * unreconciled bank statement line, so this is the honest accountant-
 * workload number, distinct from (and not part of) ACCOUNTING_STATUS_FILTER_
 * KEYS. Kept as its own export, not folded into XERO_ACCOUNTING_STATUS_
 * OPTIONS, so the backend parity test above stays a true mirror of
 * derive_accounting_status's own vocabulary.
 */
export const NOT_YET_CODED_XERO_OPTION = {
  key: 'not_yet_coded_xero',
  label: 'Not yet coded (Xero)',
  description: 'No Xero counterpart in the pull window -- Xero cannot expose an uncoded statement line.',
};

export const PENNYLANE_ACCOUNTING_STATUS_OPTIONS = [
  { key: 'queued', label: 'Queued' },
  { key: 'syncing', label: 'Syncing' },
  { key: 'refused', label: 'Refused' },
  { key: 'blocked_no_credential', label: 'No credential' },
  { key: 'attempted_failed', label: 'Failed' },
  { key: 'synced', label: 'Synced' },
  { key: 'cross_entity_blocked', label: 'Cross-entity (accountant-handled)' },
];

/**
 * Operator-facing two-value vocabulary (Fan, 28 Aug): the Accounting column
 * renders these labels, and the filter offers them as its primary choice.
 * Keys match routes/transactions.py's DISPLAY_ACCOUNTING_STATUS_KEYS and
 * services/accounting_status.py::to_display_state's display_status values.
 * The precise Xero/Pennylane statuses below stay available as diagnostics.
 */
export const DISPLAY_ACCOUNTING_STATUS_OPTIONS = [
  { key: 'reconciled', label: 'Reconciled', description: 'Confirmed reconciled in the accounting ledger.' },
  { key: 'unreconciled', label: 'Unreconciled', description: 'Anything not yet confirmed reconciled -- including feed gaps and pending pushes.' },
];
